import type { Placement } from "@floating-ui/react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@utils/tailwind.util";
import { Tooltip } from "../Tooltip";

type SwitchProps = {
  onToggle?: (isOn: boolean) => void;
  state: boolean;
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  leftTooltip?: string;
  leftTooltipContentClassName?: string;
  rightTooltip?: string;
  rightTooltipContentClassName?: string;
  circleWidth?: number;
  switchWidth?: number;
  height?: number;
  id?: string;
  isDisabled?: boolean;
  className?: string;
  leftTooltipPlacement?: Placement;
  rightTooltipPlacement?: Placement;
  isInverted?: boolean;
};

export function Switch({
  onToggle,
  state,
  leftContent,
  rightContent,
  leftTooltip = "",
  leftTooltipContentClassName = "",
  rightTooltip = "",
  rightTooltipContentClassName = "",
  circleWidth,
  switchWidth = 340,
  height = 36,
  id,
  isDisabled = false,
  leftTooltipPlacement = "top",
  rightTooltipPlacement = "top",
  isInverted,
}: SwitchProps) {
  const [isOn, setIsOn] = useState(state);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [dynamicCircleWidth, setDynamicCircleWidth] = useState<number | undefined>(circleWidth);

  // if isInverted is true then show right and left content the opposite
  const localLeftContent = isInverted ? rightContent : leftContent;
  const localRightContent = isInverted ? leftContent : rightContent;
  const localLeftTooltip = isInverted ? rightTooltip : leftTooltip;
  const localRightTooltip = isInverted ? leftTooltip : rightTooltip;
  const localLeftTooltipContentClassName = isInverted
    ? rightTooltipContentClassName
    : leftTooltipContentClassName;
  const localRightTooltipContentClassName = isInverted
    ? leftTooltipContentClassName
    : rightTooltipContentClassName;
  const localLeftTooltipPlacement = isInverted ? rightTooltipPlacement : leftTooltipPlacement;
  const localRightTooltipPlacement = isInverted ? leftTooltipPlacement : rightTooltipPlacement;

  const toggleSwitch = () => {
    if (isDisabled) return;
    const newIsOn = !isOn;
    setIsOn(newIsOn);
    onToggle?.(newIsOn);
  };

  useEffect(() => {
    setIsOn(state);
  }, [state]);

  useEffect(() => {
    // Calculate the width based on the currently selected content or use circleWidth if provided
    const currentWidth =
      circleWidth ||
      ((isOn ? rightRef.current?.offsetWidth : leftRef.current?.offsetWidth) ?? 0) + 16;
    setDynamicCircleWidth(currentWidth);
  }, [isOn, localLeftContent, localRightContent, circleWidth]);

  return (
    <div
      id={id}
      onClick={toggleSwitch}
      className={cn(
        "relative flex items-center justify-between gap-1 rounded-lg p-1 font-inter text-sm font-medium transition duration-150 ease-in-out",
        "dark:bg-gray-700 dark:text-invertedPrimary80",
        isDisabled
          ? "cursor-not-allowed bg-gray-200 dark:bg-gray-800 text-gray-400 opacity-50"
          : "cursor-pointer bg-action-secondary text-content-primary hover:bg-action-secondaryHover dark:hover:bg-gray-600 focus:border-stroke-actionPrimary focus:bg-action-secondaryHover active:border-transparent active:bg-action-secondaryPressed"
      )}
      style={{
        width: `${switchWidth}px`,
        height: `${height}px`,
        position: "relative",
      }}
    >
      {/* Left Content */}
      <div
        ref={leftRef}
        className={cn(
          "absolute flex items-center transition-opacity duration-300 ease-in-out",
          isOn ? "opacity-50" : "opacity-100"
        )}
        style={{
          left: "8px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
        }}
      >
        {localLeftTooltip ? (
          <Tooltip
            placement={localLeftTooltipPlacement}
            variant="dark"
            tooltipContent={localLeftTooltip}
            tooltipContentClassName={localLeftTooltipContentClassName}
            arrow={true}
            root={document.querySelector("[data-headlessui-portal]") as HTMLElement}
          >
            {localLeftContent}
          </Tooltip>
        ) : (
          localLeftContent
        )}
      </div>

      {/* Right Content */}
      <div
        ref={rightRef}
        className={cn(
          "absolute flex items-center transition-opacity duration-300 ease-in-out",
          isOn ? "opacity-100" : "opacity-50"
        )}
        style={{
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
        }}
      >
        {localRightTooltip ? (
          <Tooltip
            placement={localRightTooltipPlacement}
            variant="dark"
            tooltipContent={localRightTooltip}
            tooltipContentClassName={localRightTooltipContentClassName}
            arrow={true}
            root={document.querySelector("[data-headlessui-portal]") as HTMLElement}
          >
            {localRightContent}
          </Tooltip>
        ) : (
          localRightContent
        )}
      </div>

      <div
        className={cn(
          "absolute rounded-lg bg-white dark:bg-gray-500 shadow-md transition-all duration-300 ease-in-out"
        )}
        style={{
          width: dynamicCircleWidth ? `${dynamicCircleWidth}px` : "auto",
          height: `${height - 4}px`,
          zIndex: 1,
          transform: isOn
            ? `translateX(${switchWidth - (dynamicCircleWidth || 0) - 7}px)`
            : "translateX(-3px)",
        }}
      ></div>
    </div>
  );
}
